import PT from 'prop-types';
import { useContext, Suspense } from 'react';
import {
  AddBox,
  Restore,
  Save,
  FilterList,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material';
import {
  Grid2,
  Divider,
  Badge,
  Button,
  Typography,
  Chip,
  Skeleton,
  Tooltip,
  Box,
} from '@mui/material';
import { isEmpty } from 'lodash';
import { useSearchParams } from 'react-router';
import qs from 'qs';
import {
  AssetContext,
  DataTemplatesProvider,
  ParametersContext,
  ProjectContext,
  ProjectPermissionContext,
} from 'context';
import HeaderBreadcrumbs from 'routes/parameters-page/common/header-breadcrumb-tree/headerBreadcrumbs';
import AddParameters from '../../parameters-wizard';
import useParametersHeader from './hooks/get-parameter-header';

function Header({
  handleFilterActive,
  filterCount,
  setFilterCount,
  saveChanges,
  discardChanges,
  hasUnsavedRows,
  isSaving,
}) {
  const { isLoading, theAsset } = useContext(AssetContext);
  const { project } = useContext(ProjectContext);
  const { parameters, loading } = useContext(ParametersContext);
  const { writePermission } = useContext(ProjectPermissionContext);
  const [searchParams] = useSearchParams();
  const { ...parsedSearch } = qs.parse(searchParams.toString());

  const {
    handleSortDirection,
    assetsLoading,
    assetList,
    parameterWizardOpen,
    toggleParameterWizard,
    isStacked,
    headerRef,
  } = useParametersHeader(setFilterCount);

  const disabled = !!(
    !(parsedSearch.project_parameter || !isEmpty(theAsset)) ||
    theAsset?.deletedAt ||
    isLoading ||
    !writePermission ||
    (loading && isEmpty(parameters))
  );

  const renderToolTip = () => {
    if (!writePermission) return "You don't have permission to do this";
    if (theAsset?.deletedAt) return 'Cannot add parameters to deleted asset.';
    if (disabled) return 'Please select an asset';
    return 'Add parameters';
  };

  return (
    <>
      <Grid2
        container
        ref={headerRef}
        justifyContent="flex-start"
        alignItems="center"
        data-testid="parameterListHeader"
        id="paramListHeader"
        overflow="none"
        sx={{
          marginLeft: theme => theme.spacing(4),
          marginTop: theme => theme.spacing(1),
        }}
      >
        {isLoading && <Skeleton width="90%" height={50} />}
        {!isLoading && (
          <Grid2
            container
            alignItems="center"
            justifyContent="space-between"
            width="95%"
          >
            <Grid2 item>
              <Typography
                variant="h5"
                sx={{
                  margin: theme => theme.spacing(1),
                  textTransform: 'capitalize',
                }}
              >
                {theAsset?.name || project?.shortTitle}
              </Typography>
            </Grid2>
            <Grid2 item sx={{ flexGrow: 1 }}>
              <Chip
                variant="outlined"
                sx={{
                  backgroundColor: theme => theme.palette.background.paper,
                  margin: theme => theme.spacing(0.5),
                  padding: theme => theme.spacing(1),
                  borderRadius: theme => theme.spacing(1), // Make it squarer
                  fontWeight: 'bold', // Bold text
                  color: theme => theme.palette.primary.main, // Contrast text color
                  fontSize: '1rem', // Larger text
                }}
                label={
                  parsedSearch.project_parameter
                    ? 'Project'
                    : theAsset?.assetType?.name || 'Unassigned'
                }
              />
            </Grid2>
            <Grid2
              item
              sx={{
                marginRight: theme => theme.spacing(12),
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <HeaderBreadcrumbs
                assetsLoading={assetsLoading}
                assetList={assetList}
              />
            </Grid2>
          </Grid2>
        )}
        <Grid2 item size={12}>
          <Divider />
        </Grid2>
        <Box
          sx={{
            width: '95%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            margin: theme => theme.spacing(1),
            marginTop: theme => theme.spacing(2),
            flexDirection: isStacked ? 'column' : 'row',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 2,
            }}
          >
            <Tooltip title={renderToolTip()}>
              <span>
                <Button
                  variant="contained"
                  startIcon={<AddBox />}
                  disabled={disabled}
                  onClick={() => {
                    toggleParameterWizard();
                    discardChanges();
                  }}
                  data-cy={'add-button-parameter'}
                  type="parameter"
                >
                  Add Parameters
                </Button>
              </span>
            </Tooltip>
            <Tooltip
              title={
                filterCount
                  ? `${filterCount} filters applied`
                  : 'Filter and Sort'
              }
            >
              <Badge
                aria-label="filters-badge"
                color="error"
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                badgeContent={filterCount}
                overlap="circular"
                sx={{ '& .MuiBadge-badge': { right: 0 } }}
              >
                <Button
                  onClick={() => {
                    handleFilterActive();
                    discardChanges();
                  }}
                  aria-label="Filter and Sort"
                  disabled={isEmpty(parameters) && !filterCount}
                  startIcon={<FilterList />}
                >
                  Filter and Sort
                </Button>
              </Badge>
            </Tooltip>
            <Tooltip title="Sort direction">
              <Button
                disabled={isEmpty(parameters) && !filterCount}
                onClick={handleSortDirection}
                aria-label="Sort direction"
              >
                {parsedSearch.order === 'asc' ? (
                  <ArrowUpward data-testid="asc" />
                ) : (
                  <ArrowDownward data-testid="desc" />
                )}
                Sort direction
              </Button>
            </Tooltip>
          </Box>
          {hasUnsavedRows && (
            <Box
              sx={{
                display: 'flex',
                gap: 2,
              }}
            >
              <Tooltip title={'Save Changes'}>
                <span>
                  <Button
                    disabled={isSaving}
                    onClick={saveChanges}
                    title="Save"
                    startIcon={<Save />}
                  >
                    Save
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={'Discard all changes'}>
                <span>
                  <Button
                    icon={<Restore />}
                    onClick={discardChanges}
                    startIcon={<Restore />}
                  >
                    Discard all changes
                  </Button>
                </span>
              </Tooltip>
            </Box>
          )}
        </Box>
      </Grid2>
      {parameterWizardOpen && (
        <Suspense fallback={null}>
          <DataTemplatesProvider>
            <AddParameters
              open={parameterWizardOpen}
              handleToggleOpen={toggleParameterWizard}
            />
          </DataTemplatesProvider>
        </Suspense>
      )}
    </>
  );
}

Header.propTypes = {
  handleFilterActive: PT.func.isRequired,
  filterCount: PT.number,
  setFilterCount: PT.func,
  saveChanges: PT.func.isRequired,
  discardChanges: PT.func.isRequired,
  hasUnsavedRows: PT.bool.isRequired,
  isSaving: PT.bool.isRequired,
};

Header.defaultProps = {
  filterCount: 0,
  setFilterCount: () => {},
};

export default Header;
